import React, { useCallback, useEffect, useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Button, Skeleton, ToggleButton } from '@repo/ui'
import { RefreshCwIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { PageContainer, PageHeader } from 'components/UI'
import { PROSE_MAX_WIDTH } from 'constants/layout'
import { QRCodePanel } from 'components/UI/QRCodePanel'
import { QrScannerView } from 'components/UI/QrScanner'
import { useAvatar } from 'hooks'
import { usePushPage } from 'hooks/usePushPage'
import { ROUTES } from 'routes'
import { createApiCall } from 'services'
import { useSelector } from 'store/hooks'
import { extractProfileCode } from 'utils/profileCode'

/**
 * VERA-304 — the code page: your own QR, and the scanner for someone else's.
 *
 * ## Why it is a PAGE, not the section it replaced
 *
 * This was a `MY CODE` block sitting inline on Profile, between General and Log
 * in. That put a 240px card in the middle of a settings list that most visits
 * are not there for, and it had nowhere to put the other half of the same
 * interaction — reading a code — because a scanner is not a settings row.
 *
 * As its own pushed page the two halves sit side by side under one control,
 * which is the shape the interaction actually has: showing and reading a code
 * are the two ends of the same exchange, and whichever end you are is decided in
 * the moment, by which phone is being held up.
 *
 * ## Why the photo is in the middle of the code
 *
 * A QR is unreadable to a human: two people holding up two codes cannot tell
 * which is whose, and the failure this whole feature exists to prevent is a gift
 * sent to the wrong person (VERA-304's own verification sent one to the wrong
 * "John Doe"). The face in the centre makes the code self-identifying BEFORE it
 * is scanned. It costs error-correction budget, which is why `QRCodePanel`
 * couples the inset to level `H` rather than leaving them as two settings.
 */
export const MyCodePage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pageSx, goBack } = usePushPage(ROUTES.PROFILE)
  const { resolveAvatarUrl } = useAvatar()
  const { profile } = useSelector(({ auth }) => auth)

  const [mode, setMode] = useState<'code' | 'scan'>('code')
  const [code, setCode] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [isResetting, setIsResetting] = useState<boolean>(false)
  /** Reset failure, shown in the confirm sheet it happened in. See `resetCode`. */
  const [resetError, setResetError] = useState<string | null>(null)
  /** Verdict on the last scanned code, handed to the scanner's inline slot. */
  const [scanNotice, setScanNotice] = useState<string | null>(null)
  /**
   * Rejected-scan count, used as the scanner's `key` so a rejection remounts it
   * and the camera comes back. A counter rather than the notice string itself:
   * rescanning the same bad code produces the same string, which React treats as
   * no change (see QrScannerViewProps.notice).
   */
  const [scanAttempt, setScanAttempt] = useState(0)

  /*
   * `createApiCall` does NOT reject on failure unless `throwError` is passed —
   * it hands the error response back through the same resolve path (see
   * services/index.ts errorHandler). So a try/catch alone would treat a 500 as
   * a success with `data.code === undefined`; the shape of `data` is what has
   * to be checked.
   */
  const loadCode = useCallback(async () => {
    setLoadFailed(false)
    const res = await createApiCall({ method: 'GET', url: '/auth/customer/profile-code' })
    const next = typeof res?.data?.code === 'string' ? res.data.code : null
    if (!next) {
      setLoadFailed(true)
      return
    }
    setCode(next)
  }, [])

  useEffect(() => {
    loadCode()
  }, [loadCode])

  const resetCode = async () => {
    setIsResetting(true)
    setResetError(null)
    const res = await createApiCall({ method: 'POST', url: '/auth/customer/profile-code/rotate' })
    const next = typeof res?.data?.code === 'string' ? res.data.code : null
    setIsResetting(false)

    if (!next) {
      /*
       * INLINE, inside the sheet the user is still standing in — not a toast.
       *
       * A failure has to be attached to the thing that failed, and the sheet is
       * open with its confirm button right there: the message belongs beside the
       * button you would press again. A toast puts it outside the modal it is
       * about, on a timer, which is how an error gets missed.
       */
      setResetError('Could not reset your code. Try again.')
      return
    }

    setCode(next)
    setLoadFailed(false)
    setConfirmOpen(false)
    /*
     * NO success feedback, and that is the rule rather than an omission.
     *
     * The sheet closes and a visibly different QR is on screen — the effect of
     * the action IS the screen, so a toast would only narrate what the user is
     * already looking at (toast policy: visible effect, no toast).
     *
     * This reverses an earlier note here arguing the change was not
     * "recognisable" enough (one block of modules resembling another) and
     * therefore earned a toast. That argument proves too much: the user did not
     * ask to memorise the pattern, they asked for a new code, and the modal they
     * just confirmed through already told them what would happen. What they need
     * back is the closed sheet, which they get.
     */
  }

  /*
   * A scan lands on the PUBLIC card for that code (`/u/:code`), which already
   * resolves it, shows whose it is and offers the way on to the gift flow.
   * Re-resolving it here would be a second copy of that page's logic and a
   * second set of failure messages for the same 404.
   */
  const handleScanned = (raw: string) => {
    const scanned = extractProfileCode(raw)
    if (!scanned) {
      /*
       * Handed to the SCANNER's inline slot rather than raised as a toast. The
       * scanner owns that surface and already shows its own decode failures
       * there, so this keeps one message region instead of a toast floating over
       * it — and setting it also restarts the camera, which a decode had
       * stopped — the `scanAttempt` bump remounts it, which is what turns the
       * camera back on (see QrScannerView's `notice`).
       */
      setScanNotice(
        "That code didn't match anyone. Ask them to open Vera, then Profile, then My code for a fresh one.",
      )
      setScanAttempt(a => a + 1)
      return
    }
    navigate(`/u/${encodeURIComponent(scanned)}`)
  }

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim()
  const handle = profile?.username ? `@${profile.username}` : null
  const avatarUrl = resolveAvatarUrl(profile?.avatar)
  const initials = `${profile?.first_name?.slice(0, 1) ?? ''}${
    profile?.last_name?.slice(0, 1) ?? ''
  }`.toUpperCase()

  return (
    <Stack
      sx={{
        background: theme.palette.background.paper,
        minHeight: '100vh',
        // NO `px` — the side gutter belongs to `PageContainer` below.
        pt: { xs: 4, md: 6 },
        pb: { xs: 4, md: 8 },
        ...pageSx,
      }}
    >
      {/* Shared column outside, READING measure inside — see Profile's note. */}
      <PageContainer>
      <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
        <PageHeader backLabel='Profile' backTo={ROUTES.PROFILE} onBack={goBack} title='My code' />

        {/*
          The shared `@repo/ui` ToggleButton, not a hand-rolled segmented control
          (working-conventions §28/§47) — the same one the certificate page's
          DisplayedDataPopover uses, so the two read as one control.
        */}
        <ToggleButton
          fullWidth
          tab={mode}
          onChange={value => setMode(value as 'code' | 'scan')}
          tabs={[
            { label: 'My code', value: 'code' },
            { label: 'Scan', value: 'scan' },
          ]}
        />

        {mode === 'code' ? (
          // 24px under the toggle, then 16px between the identity lines and the
          // card. `apps/app` is on MUI's default 8px base, NOT @repo/ui's 4px
          // (§29), so these factors are half what the same gap would be in the
          // platform.
          <Stack alignItems='center' spacing={2} sx={{ mt: 3 }}>
            {/*
              Name over handle, ABOVE the card, and the card carries no caption.
              The identity belongs to the person, not to the code — and the
              handle rendered in both places was the same string twice.
            */}
            {profile ? (
              <Stack alignItems='center' spacing={0.5}>
                {fullName && (
                  <Typography sx={{ fontSize: '20px', lineHeight: 1.4, textAlign: 'center' }}>
                    {fullName}
                  </Typography>
                )}
                {handle && (
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}
                  >
                    {handle}
                  </Typography>
                )}
              </Stack>
            ) : (
              // §19 — the same two-line footprint the resolved identity takes,
              // so nothing shifts when `profile` lands.
              <Stack alignItems='center' spacing={0.5}>
                <Skeleton variant='text' width={160} height={28} />
                <Skeleton variant='text' width={110} height={20} />
              </Stack>
            )}

            <QRCodePanel
              value={code ? `${window.location.origin}/u/${code}` : undefined}
              insetImageUrl={avatarUrl || undefined}
              insetImageAlt={fullName ? `${fullName}'s photo` : 'Your profile photo'}
              // Falls back to initials so the code is self-identifying (and at
              // the same error-correction level) whether or not a photo is set.
              insetInitials={initials || undefined}
            />

            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.secondary,
                // 1.5 so descriptive copy does not read dense (§15).
                lineHeight: 1.5,
                textAlign: 'center',
                maxWidth: 320,
              }}
            >
              Let someone scan this to send you a certificate.
            </Typography>

            {loadFailed ? (
              // Same slot, so the same size as the control it stands in for.
              <Button variant='secondary' onClick={loadCode}>
                Try again
              </Button>
            ) : (
              /*
                SECONDARY, at the default (40px) height — both changed here
                (VERA-304 follow-up, user directive).

                It shipped destructive, was reversed to primary, and is now
                secondary. The through-line is that each step took one more
                degree of emphasis off a control that never earned it: the
                destructive tier is for actions that DESTROY a record (§32, §38's
                test — does a row disappear?), and resetting mints a new code and
                retires the old one while the account, the profile and every
                certificate stand. But `primary` was still wrong for a different
                reason: a primary is the page's CALL TO ACTION, and the call to
                action on this page is showing the code, which is the QR itself.
                A solid black button under it made the one thing you should
                rarely do the loudest thing on screen.

                Secondary also puts it in the same family as `Try again` above,
                which occupies the identical slot.

                Size: 40, the Button default — the same height as the toggle's
                halves at the top of the page and as the buttons in the confirm
                sheet. `small` (32) made it read as a footnote to the QR rather
                than a control (measured 34px rendered against everything else's
                40).

                The confirm stays, because the consequence is real and is about
                the copies already in circulation — a thing a button colour
                cannot say and a modal can (§43).
              */
              <Button
                variant='secondary'
                onClick={() => setConfirmOpen(true)}
                startIcon={<RefreshCwIcon size={18} strokeWidth={1.5} />}
              >
                Reset code
              </Button>
            )}
          </Stack>
        ) : (
          <Box sx={{ mt: 3 }}>
            {/*
              The SAME scanner the gift picker uses, rendered without its sheet
              (see QrScannerView). Mounted only while this half is selected, so
              switching back to the code releases the camera rather than leaving
              the hardware indicator lit behind a hidden panel.
            */}
            <QrScannerView
              key={scanAttempt}
              active
              onDecoded={handleScanned}
              notice={scanNotice}
            />
          </Box>
        )}
      </Box>
      </PageContainer>

      {confirmOpen && (
        <DrawerPopup
          title='Reset code'
          titleUppercase={false}
          fullScreen={false}
          titleSx={{ color: theme.palette.text.primary, fontSize: '20px', fontWeight: 'normal' }}
          backgroundColor={theme.palette.background.paper}
          handleOpen={confirmOpen}
          handleClose={() => {
            setResetError(null)
            setConfirmOpen(false)
          }}
          child={
            <>
              {/*
                The gate NAMES the consequence rather than asking "are you sure?"
                (§43). What makes this worth confirming is not that the code
                changes, it is that copies of the old one are already out in the
                world — printed, messaged, screenshotted — and this is the only
                moment the user can be told they will stop working.
              */}
              <Typography variant='body1' sx={{ textAlign: 'center', lineHeight: 1.5 }}>
                Your current code stops working straight away. Anyone who saved it or
                took a screenshot will no longer be able to reach you with it. You will
                get a new code to share.
              </Typography>
              {/*
                The failure lands HERE, above the button that produced it and
                inside the sheet the user is still in — not in a toast outside
                it. Only rendered when it happens, so nothing shifts in the
                normal case.
              */}
              {resetError && (
                <Typography
                  role='status'
                  variant='body2'
                  sx={{
                    textAlign: 'center',
                    lineHeight: 1.5,
                    mt: 2,
                    color: theme.palette.error.main,
                  }}
                >
                  {resetError}
                </Typography>
              )}
              {/* 12px between the two buttons — `apps/app` is on the 8px base (§29). */}
              <Stack gap={1.5} mt={3}>
                {/*
                  PRIMARY here, even though its trigger is now secondary — the
                  two answer different questions (§32). The trigger sits in
                  persistent page chrome where emphasis is a claim about the
                  page's main action; this button is the commit step of a
                  transient sheet the user opened deliberately, where the primary
                  IS the one action on offer.
                */}
                <Button fullWidth variant='primary' loading={isResetting} onClick={resetCode}>
                  Reset code
                </Button>
                <Button
                  fullWidth
                  variant='secondary'
                  onClick={() => {
                    setResetError(null)
                    setConfirmOpen(false)
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </>
          }
        />
      )}
    </Stack>
  )
}

export default MyCodePage
